import {Office, Role} from "../../api/Model";
import {BERLIN_OFFICE_PATH_ELEMENT} from "../OfficePath";
import {rolePermissions} from "./AccessRules";
import {Offices_Read} from "./Permission";

type OfficeAccess =
    | { type: 'none' }
    | { type: 'some', officeIds: ReadonlySet<Office['id']> }
    | { type: 'all' }
    ;

export class AccessibleOffices {
    private readonly officeAccess: OfficeAccess;

    constructor(roles: readonly Role[]) {
        this.officeAccess = AccessibleOffices.accessibleOffices(roles);
    }

    canAccessOffice(officeId: Office['id']): boolean {
        switch (this.officeAccess.type) {
            case 'none':
                return false;
            case 'all':
                return true;
            case 'some':
                return this.officeAccess.officeIds.has(officeId);
        }
    }

    getDefaultOfficePath(): string | null {
        switch (this.officeAccess.type) {
            case "none":
                return null;
            case "some":
                return this.officeAccess.officeIds.values().next().value?.toString() ?? null;
            case "all":
                return BERLIN_OFFICE_PATH_ELEMENT;
        }
    }

    private static none: OfficeAccess = {type: 'none'} as const;
    private static all: OfficeAccess = {type: 'all'} as const;

    private static accessibleOffices(roles: readonly Role[]): OfficeAccess {
        return roles
            .filter(role => rolePermissions[role.type].has(Offices_Read))
            .reduce((officeAccess: OfficeAccess, role) => {
                switch (role.type) {
                    case "guest":
                        return officeAccess;
                    case "system-admin":
                        return AccessibleOffices.all;
                    case "office-admin":
                    case "vault-manager":
                        switch (officeAccess.type) {
                            case "none":
                                return {
                                    type: 'some',
                                    officeIds: new Set([role.officeId]),
                                };
                            case "some":
                                return {
                                    type: 'some',
                                    officeIds: new Set([
                                        ...officeAccess.officeIds,
                                        role.officeId,
                                    ]),
                                };
                            case "all":
                                return AccessibleOffices.all;
                        }
                }
                return officeAccess;
            }, AccessibleOffices.none);
    }
}
